import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { api } from "../../lib/api.js";

/**
 * Query-key roots the server scopes to the session's active team. A real
 * team switch RESETS these (not invalidates — invalidation keeps painting
 * the old team's data until the refetch lands). Doc-scoped roots ("doc",
 * "versions", …) are deliberately absent: a deck's content doesn't depend
 * on the active team, and refetching ["doc", id] re-pulls MB of deck HTML.
 * Register any new team-scoped query root here.
 */
const TEAM_SCOPED_ROOTS = new Set([
	"docs",
	"billing",
	"brand",
	"notifications",
	// The new-deck dialog's style picker — a stale team's styles would submit a
	// group the new team doesn't own (400 invalid_style).
	"deck-styles",
	// Native team management (members/invites/domains of the ACTIVE team).
	"team-members",
	"team-invites",
	"team-domains",
	// Deck folders belong to one team. Same failure mode as "deck-styles":
	// filing a deck with a stale team's folder id is a 400 invalid_folder.
	"folders",
]);
export const isTeamScoped = (query: {
	queryKey: readonly unknown[];
}): boolean => {
	const root = query.queryKey[0];
	if (typeof root !== "string") return false;
	// Brand Studio roots are hyphenated ("brand-groups", "brand-health", …) —
	// prefix-match so new brand-* roots stay covered without registry drift.
	return TEAM_SCOPED_ROOTS.has(root) || root.startsWith("brand-");
};

/**
 * Switch the active team: server session first (the single source of truth),
 * then drop team-scoped caches so the old team's decks/billing/brand are
 * never painted, and refetch `me` (invalidate, not reset — the auth gate
 * keys off me.data, so it must keep its data while revalidating).
 * Throws on failure so callers can toast.
 */
export function useSwitchTeam(): (orgId: string | null) => Promise<void> {
	const qc = useQueryClient();
	return useCallback(
		async (orgId: string | null) => {
			await api.setActiveOrg(orgId);
			// Cancel first so an in-flight response fetched under the old team
			// can't land after the reset.
			await qc.cancelQueries({ predicate: isTeamScoped });
			await Promise.all([
				qc.resetQueries({ predicate: isTeamScoped }),
				qc.invalidateQueries({ queryKey: ["me"] }),
			]);
		},
		[qc],
	);
}
